import {
  ACCOUNT_ERROR_CODES,
  ACCOUNT_TYPE_CODES,
  ADVERSE_ASSET_CLASSIFICATIONS,
  CIBIL_REMARKS_CODES,
  COLLATERAL_TYPE_CODES,
  ENQUIRY_PURPOSE_CODES,
  NEUTRAL_CONDITION_LABELS,
  NON_DPD_PAY_STATUS,
  OWNERSHIP_CODES,
  PAYMENT_FREQUENCY_CODES,
  accountConditionLabel,
  assetClassificationLabel,
  codeLabel,
} from "@/lib/cibil/codes";
import { isBlank, toDate, toNumber } from "./format";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Raw = any;

export interface PaymentPoint {
  date: Date;
  /** Numeric days-past-due, or null when the status carries no DPD. */
  dpd: number | null;
  status: string;
  /** Asset Classification code (STD/SMA/SUB/DBT/LSS/XXX) when reported. */
  classificationCode: string | null;
  /** Human label for the Asset Classification, when reported. */
  classification: string | null;
}

export interface NormalizedAccount {
  id: string;
  investorName: string | null;
  accountNumber: string | null;
  loanTypeCode: string | null;
  loanType: string | null;
  sanctionDate: Date | null;
  closedDate: Date | null;
  lastReported: Date | null;
  sanctionedLimit: number | null;
  outstanding: number | null;
  currentOverdue: number | null;
  creditLimit: number | null;
  emi: number | null;
  tenureMonths: number | null;
  frequency: string | null;
  ownership: string | null;
  collateral: string | null;
  remarks: string[];
  /** Decoded dispute descriptions for this account. */
  disputes: string[];
  /** Worst Asset Classification reported in the payment history. */
  worstClassification: string | null;
  payments: PaymentPoint[];
  dpdCount: number;
  totalDpdDays: number;
  maxDpd: number | null;
  isActive: boolean;
  isRevolving: boolean;
  /** Date of the last payment reported by the lender. */
  lastPaymentDate: Date | null;
  /** Last actual amount paid, when reported. */
  lastPaymentAmount: number | null;
  /** Date the account status was last set. */
  statusDate: Date | null;
  /** Reporting window covered by the payment history. */
  reportingWindow: { start: Date | null; end: Date | null };
  /** True when the account carries collateral or is a secured product. */
  isSecured: boolean;
  interestRate: number | null;
  /** Decoded AccountCondition where abbreviation = "creditFacilityStatus". */
  creditFacilityStatus: string | null;
  /** Decoded Suit Filed / Wilful Default status. */
  suitFiledStatus: string | null;
}

export interface NormalizedEnquiry {
  id: string;
  date: Date | null;
  lender: string | null;
  purposeCode: string | null;
  purpose: string | null;
  amount: number | null;
}

export interface NormalizedReport {
  generatedAt: Date | null;
  borrowerName: string | null;
  creditScore: number | null;
  accounts: NormalizedAccount[];
  enquiries: NormalizedEnquiry[];
  bureauUtilisation: number | null;
}

function asArray<T>(v: T | T[] | null | undefined): T[] {
  if (v === null || v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

function sym(node: Raw): string | null {
  if (!node) return null;
  if (typeof node === "string") return isBlank(node) ? null : node;
  const s = node.symbol;
  return isBlank(s) ? null : String(s);
}

function abbr(node: Raw): string | null {
  if (!node || typeof node === "string") return null;
  return isBlank(node.abbreviation) ? null : String(node.abbreviation);
}

function parseStatus(status: unknown): number | null {
  if (status === null || status === undefined) return null;
  const s = String(status).trim().toUpperCase();
  if (NON_DPD_PAY_STATUS.has(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Decodes CIBIL remarks / dispute codes attached to a node. */
function decodeRemarkCodes(nodes: Raw[]): string[] {
  const out: string[] = [];
  for (const node of nodes) {
    const code = sym(node);
    if (!code) continue;
    const trimmed = code.trim();
    if (trimmed === "0" || trimmed === "00" || trimmed === "000000") continue;
    const label =
      CIBIL_REMARKS_CODES[trimmed] ??
      ACCOUNT_ERROR_CODES[trimmed] ??
      ACCOUNT_ERROR_CODES[trimmed.replace(/^0+/, "")] ??
      `Remark ${trimmed}`;
    if (label !== "No Disputes" && !out.includes(label)) out.push(label);
  }
  return out;
}

/** Account type codes that are secured products by definition (guide V3.5). */
const SECURED_TYPE_CODES = new Set([
  "01", "02", "03", "04", "07", "11", "13", "15", "16", "17", "23", "31",
  "32", "33", "34", "36", "42", "46", "50", "53", "59", "70", "98",
]);

function normalizeTradeline(part: Raw, index: number): NormalizedAccount {
  const t = part?.Tradeline ?? part ?? {};
  const g = t.GrantedTrade ?? {};

  const payments: PaymentPoint[] = asArray(g?.PayStatusHistory?.MonthlyPayStatus)
    .concat(asArray(t?.paymentHistory))
    .map((p: Raw) => {
      const date = toDate(p?.date ?? p?.paymentDate);
      const status = String(p?.status ?? p?.paymentStatus ?? "");
      if (!date) return null;
      const code = status.trim().toUpperCase();
      const classification = assetClassificationLabel(code);
      return {
        date,
        dpd: parseStatus(status),
        status,
        classificationCode: classification ? code : null,
        classification,
      };
    })
    .filter((p): p is PaymentPoint => p !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const dpds = payments.map((p) => p.dpd).filter((d): d is number => d !== null);

  const worstCode = ADVERSE_ASSET_CLASSIFICATIONS.reduce<string | null>(
    (worst, code) => (payments.some((p) => p.classificationCode === code) ? code : worst),
    null,
  );
  const worstClassification = worstCode ? assetClassificationLabel(worstCode) : null;

  const loanTypeCode =
    sym(g.AccountType) ??
    sym(part?.accountTypeSymbol) ??
    sym(t.accountType) ??
    sym(part?.AccountType);

  const closedDate = toDate(t.dateClosed);

  const disputes = decodeRemarkCodes([
    t.DisputeFlag,
    ...asArray(t.Remark),
    ...asArray(t.CibilRemarks),
    ...asArray(g.Remark),
  ]);

  const remarks: string[] = [];
  const settled = toNumber(t.settlementAmount ?? g.settlementAmount);
  const writtenOff = toNumber(t.writtenOffAmtTotal ?? g.writtenOffAmtTotal);
  const condition = accountConditionLabel(sym(t.AccountCondition), abbr(t.AccountCondition));
  const suitFiled = accountConditionLabel(sym(t.SuitFiledStatus), "suitFiledStatus");
  const payStatus = sym(t.PayStatus) ?? sym(g.WorstPayStatus);
  const payStatusLabel = assetClassificationLabel(payStatus) ?? payStatus;

  if (settled) remarks.push("Settled");
  if (writtenOff) remarks.push("Written Off");
  if (!isBlank(t.accountsSoldTo)) remarks.push(`Sold to ${t.accountsSoldTo}`);
  if (condition && !NEUTRAL_CONDITION_LABELS.has(condition)) remarks.push(condition);
  if (suitFiled && !NEUTRAL_CONDITION_LABELS.has(suitFiled)) remarks.push(suitFiled);
  if (worstClassification) remarks.push(worstClassification);
  if (payStatusLabel) remarks.push(payStatusLabel);
  for (const d of disputes) remarks.push(d);

  const creditLimit = toNumber(g.CreditLimit ?? t.creditLimit);
  const sanctionedLimit =
    toNumber(t.highBalance ?? t.highCreditAmount) ?? creditLimit;

  const collateralCode = sym(g.CollateralType) ?? sym(t.CollateralType);
  const collateral = codeLabel(COLLATERAL_TYPE_CODES, collateralCode, "Collateral");
  const hasCollateral = collateralCode !== null && collateralCode.trim() !== "00";

  return {
    id: String(t.accountNumber ?? g.serialNumber ?? index),
    investorName: isBlank(t.creditorName ?? t.memberShortName)
      ? null
      : String(t.creditorName ?? t.memberShortName),
    accountNumber: isBlank(t.accountNumber) ? null : String(t.accountNumber),
    loanTypeCode,
    loanType: codeLabel(ACCOUNT_TYPE_CODES, loanTypeCode, "Type"),
    sanctionDate: toDate(t.dateOpened),
    closedDate,
    lastReported: toDate(t.dateReported ?? t.reportedDate),
    sanctionedLimit,
    outstanding: toNumber(t.currentBalance),
    currentOverdue: toNumber(g.amountPastDue ?? t.amountOverdue ?? t.amountPastDue) ?? 0,
    creditLimit,
    emi: toNumber(g.EMIAmount ?? t.EMIAmount),
    tenureMonths: toNumber(g.termMonths ?? t.repaymentTenure),
    frequency: codeLabel(
      PAYMENT_FREQUENCY_CODES,
      sym(g.PaymentFrequency) ?? sym(t.paymentFrequency),
      "Frequency",
    ),
    ownership: codeLabel(
      OWNERSHIP_CODES,
      sym(t.AccountDesignator) ?? sym(t.ownershipIndicator),
      "Ownership",
    ),
    collateral,
    remarks: [...new Set(remarks)],
    disputes,
    worstClassification,
    payments,
    dpdCount: dpds.length,
    totalDpdDays: dpds.reduce((a, b) => a + b, 0),
    maxDpd: dpds.length ? Math.max(...dpds) : null,
    isActive: closedDate === null,
    isRevolving: loanTypeCode === "10" || loanTypeCode === "12" || loanTypeCode === "31" || loanTypeCode === "35",
    lastPaymentDate: toDate(g.dateLastPayment ?? t.dateLastPayment),
    lastPaymentAmount: toNumber(g.actualPaymentAmount ?? t.actualPaymentAmount),
    statusDate: toDate(t.dateAccountStatus ?? g.dateAccountStatus),
    reportingWindow: {
      start: toDate(g?.PayStatusHistory?.startDate),
      end: toDate(g?.PayStatusHistory?.endDate),
    },
    isSecured:
      hasCollateral ||
      (loanTypeCode !== null && SECURED_TYPE_CODES.has(loanTypeCode.padStart(2, "0"))),
    interestRate: toNumber(g.interestRate ?? t.interestRate),
    creditFacilityStatus:
      abbr(t.AccountCondition) === "suitFiledStatus" ? null : condition,
    suitFiledStatus:
      suitFiled ??
      (abbr(t.AccountCondition) === "suitFiledStatus" ? condition : null),
  };
}

function normalizeEnquiry(part: Raw, index: number): NormalizedEnquiry {
  const q = part?.Inquiry ?? part ?? {};
  const code = isBlank(q.inquiryType) ? null : String(q.inquiryType);
  return {
    id: String(q.enqControlNum ?? index),
    date: toDate(q.inquiryDate ?? q.dateOfInquiry),
    lender: isBlank(q.subscriberName) ? null : String(q.subscriberName),
    purposeCode: code,
    purpose: codeLabel(ENQUIRY_PURPOSE_CODES, code, "Purpose"),
    amount: toNumber(q.amount),
  };
}


/** Unwrap TrustHub bureau-report envelope → TrueLink report shape. */
function unwrapBureauPayload(raw: Raw): Raw {
  const asset =
    raw?.result?.cibilData?.GetCustomerAssetsResponse?.GetCustomerAssetsSuccess
      ?.Asset ??
    raw?.cibilData?.GetCustomerAssetsResponse?.GetCustomerAssetsSuccess?.Asset;

  if (asset?.TrueLinkCreditReport) {
    return {
      generatedAt: raw?.datetime ?? asset?.CreationDate ?? raw?.generatedAt,
      report: asset.TrueLinkCreditReport,
      TrueLinkCreditReport: asset.TrueLinkCreditReport,
      creditSummary:
        raw?.result?.cibilData?.GetCustomerAssetsResponse
          ?.GetCustomerAssetsSuccess?.CreditSummaryData ??
        raw?.cibilData?.GetCustomerAssetsResponse?.GetCustomerAssetsSuccess
          ?.CreditSummaryData,
    };
  }
  return raw;
}

export function normalizeReport(raw: Raw): NormalizedReport {
  const unwrapped = unwrapBureauPayload(raw);
  const report =
    unwrapped?.report ?? unwrapped?.TrueLinkCreditReport ?? unwrapped ?? {};
  const subjectAccounts = unwrapped?.subject?.[0]?.account;

  const rawAccounts: Raw[] = subjectAccounts
    ? asArray(subjectAccounts)
    : asArray(report.TradeLinePartition);
  const rawEnquiries: Raw[] = unwrapped?.subject?.[0]?.inquiry
    ? asArray(unwrapped.subject[0].inquiry)
    : asArray(report.InquiryPartition);

  const accounts = rawAccounts.map(normalizeTradeline);
  const enquiries = rawEnquiries
    .map(normalizeEnquiry)
    .sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));

  const borrower = report.Borrower ?? {};
  const score = toNumber(borrower?.CreditScore?.riskScore);
  const creditSummary =
    unwrapped?.creditSummary ??
    unwrapped?.CreditSummaryData ??
    raw?.result?.cibilData?.GetCustomerAssetsResponse?.GetCustomerAssetsSuccess
      ?.CreditSummaryData;
  const bureauUtilisation = toNumber(
    creditSummary?.CreditCardUtilization ?? creditSummary?.creditUtilization,
  );

  return {
    generatedAt:
      toDate(unwrapped?.generatedAt) ??
      accounts.find((a) => a.lastReported)?.lastReported ??
      null,
    borrowerName: borrower?.BorrowerName?.Name?.Forename
      ? String(borrower.BorrowerName.Name.Forename)
      : null,
    creditScore: score,
    accounts,
    enquiries,
    bureauUtilisation,
  };
}
